import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import Button from "@/design-system/button";
import { CashbackDetailsResources } from "@/src/cashback/CashbackDetails/CashbackDetails.Resources";
import { CashbackDetailsResourcesEn } from "@/src/cashback/CashbackDetails/CashbackDetails.Resources.En";
import CashbackDetailsV2 from "@/src/cashback/CashbackDetailsV2";
import { TransitionLink } from "@/src/common/Transition";
import PenSVG from "@/src/common/icons/PenSVG";
import TickSVG from "@/src/common/icons/TickSVG";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { type ILink, LinkAppearance } from "@Components/Link";
import { Modal } from "@Components/Modal";
import StickTopPanel from "@Components/StickTopPanel";
import * as stylex from "@stylexjs/stylex";

import { type QueryXsMax, globalAccessibility } from "../../../styles/tokens.stylex";
import { ComposableSpacing, colors, genericStyles } from "../../../styles/tokens.stylex";
import PlnIcon from "../../common/icons/PlnIcon";
import QuestionMarkSVG from "../../common/icons/QuestionMarkSVG";
import type { ISpecialCashback } from "../CashbackCommonInterfaces";
import type { IGoToShopButton } from "../GoToShopButton";
import { GoToShopButton } from "../GoToShopButton";

import * as css from "./CashbackDetails.module.css";

export interface ICashbackDetails {
    brandId: string;
    brandName: string;
    logoUrl: string;
    cashbackAdditionalInfo: ICashbackAdditionalInfo;
    navigateButton?: IGoToShopButton;
    cashback: string;
    modalTitle: string;
    infoLink: ILink;
    cashbackAdditionalInfoHeader: string;
    complementaryInfo: string;
    extraCashbackInfo?: INewCashbackBonusInfo;
    specialCashback?: ISpecialCashback;
    offerExtraInfo?: string;
}

interface ICashbackAdditionalInfo {
    trackingInfo: ICashbackAdditionalInfoElement;
    approvalInfo: ICashbackAdditionalInfoElement;
    cashbackList: string[];
}

interface ICashbackAdditionalInfoElement {
    title: string;
    subtitle: string;
}

interface INewCashbackBonusInfo {
    title: string;
    description: string;
}

const mapCashbackList = (value: string, index: number) => (
    <li key={index} dangerouslySetInnerHTML={{ __html: value }} />
);

const buildAdditionalInfo = (item: ICashbackAdditionalInfoElement, svgIcon: JSX.Element) =>
    item && (
        <div className={css.cashbackAdditionalInfoContent}>
            <div className={css.cashbackAdditionalInfoIcon}>{svgIcon}</div>
            <div className={css.cashbackAdditionalInfo}>
                <div className={css.cashbackAdditionalInfoTitle}>{item.title}</div>
                <div className={css.cashbackAdditionalInfoSubtitle}>{item.subtitle}</div>
            </div>
        </div>
    );

export default function CashbackDetails(props: ICashbackDetails) {
    const [isOpen, setIsOpen] = useState(false);
    const { isMobile } = useViewportInfoContext();
    const { pathname } = useLocation();
    const [search] = useSearchParams();
    const cashbackLabelId = "cb-brand-details";

    const toggleModal = () => setIsOpen(!isOpen);

    const resources = languageSelector(CashbackDetailsResources, CashbackDetailsResourcesEn);

    const renderSpecialOfferLabel = props.specialCashback && (
        <div className={`${css.superLabel} ${stylex.props(styles.border25).className}`}>
            {resources.specialCashbackBadgeTitle}
        </div>
    );

    useEffect(() => {
        if (pathname.includes("cashback") || search.get("utm_source") === "onet") {
            setIsOpen(true);
        }
    }, [pathname, search]);

    const renderExtraInfo = (
        <>
            <span className={css.complement}>{props.complementaryInfo}</span>
            {props.offerExtraInfo && (
                <div className={css.extraInfo} dangerouslySetInnerHTML={{ __html: props.offerExtraInfo }} />
            )}
        </>
    );

    const renderNewCashbackInfo = props.extraCashbackInfo && (
        <>
            <span {...stylex.props(styles.plus)}>+</span>
            <Button
                as="button"
                variant="none"
                size="None"
                onClick={toggleModal}
                styleX={[styles.newCashbackUser, styles.buttons]}
                role="button"
            >
                <span {...stylex.props(styles.newUserText, styles.title)} className={css.title}>
                    {props.extraCashbackInfo.title}
                </span>
                <span {...stylex.props(styles.newUserText, styles.title)}>
                    {props.extraCashbackInfo.description}
                </span>
            </Button>
        </>
    );

    const modal = () => {
        if (search.get("utm_source")) {
            return (
                <CashbackDetailsV2
                    isOpen={isOpen}
                    brandId={props.navigateButton.brandId}
                    brandName={props.brandName}
                    logoUrl={props.logoUrl}
                    cashbackAdditionalTrackingInfo={props.cashbackAdditionalInfo.trackingInfo}
                    cashbackAdditionalApprovalInfo={props.cashbackAdditionalInfo.approvalInfo}
                    cashbackList={props.cashbackAdditionalInfo.cashbackList}
                    cashback={props.cashback}
                    cashbackAdditionalInfoHeader={props.cashbackAdditionalInfoHeader}
                    specialCashback={props.specialCashback}
                    onClose={toggleModal}
                />
            );
        }

        const [desc, ...list] = props.cashbackAdditionalInfo?.cashbackList ?? [];
        const footer = list.pop();

        return (
            <Modal isOpen={isOpen} className={css.cashbackDetailsModal} onClose={toggleModal} allowBodyScroll>
                <h2>{props.modalTitle}</h2>
                {desc && <div className={css.descriptions} dangerouslySetInnerHTML={{ __html: desc }} />}
                <ul>{list.map(mapCashbackList)}</ul>
                {footer && <div className={css.descriptions} dangerouslySetInnerHTML={{ __html: footer }} />}
                <div className={css.cashbackAdditionalInfoDetails}>
                    <div className={css.cashbackAdditionalHeader}>{props.cashbackAdditionalInfoHeader}</div>
                    {buildAdditionalInfo(props.cashbackAdditionalInfo.trackingInfo, <PenSVG />)}
                    {buildAdditionalInfo(props.cashbackAdditionalInfo.approvalInfo, <TickSVG />)}
                </div>
                <TransitionLink
                    {...props.infoLink}
                    appearance={LinkAppearance.LightBlue}
                    className={css.modalLink}
                />
            </Modal>
        );
    };
    const isSpecialOffer = !!props.specialCashback;

    const cbAriaLabel = isSpecialOffer
        ? resources.cbSpecialAriaLabel
              .replace("{0}", props.specialCashback?.maxCashback || "")
              .replace("{1}", props.cashback || "")
        : resources.cbAriaLabel.replace("{0}", props.cashback || "");

    return (
        <div className={css.cashbackDetailsComponent}>
            <div className={`${css.contentContainer} ${stylex.props(styles.border).className}`}>
                {renderSpecialOfferLabel}

                <div className={css.details} tabIndex={0}>
                    <div className={css.cashbackHeader}>
                        <PlnIcon styleX={styles.cashbackIcon} aria-hidden />

                        <div className={css.middleContainer} role="text" tabIndex={0}>
                            <span
                                id={cashbackLabelId}
                                {...stylex.props(globalAccessibility.ScreenReadersOnly)}
                            >
                                {cbAriaLabel}
                            </span>
                            {isSpecialOffer ? (
                                <div className={css.cashbackHotDeal}>
                                    <span className={css.newCashback} aria-hidden="true">
                                        {props.specialCashback?.maxCashback}
                                    </span>
                                    {props.specialCashback.overrideSegmentMaxCashback && (
                                        <span className={css.previousCashback} aria-hidden="true">
                                            {props.cashback}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <h2 className={css.title} aria-labelledby={cashbackLabelId}>
                                    <span aria-hidden="true">{props.cashback}</span>
                                </h2>
                            )}

                            {isMobile === false && renderExtraInfo}
                        </div>

                        <Button
                            as="button"
                            variant="none"
                            size="None"
                            onClick={toggleModal}
                            styleX={styles.infoIcon}
                        >
                            <QuestionMarkSVG />
                        </Button>
                    </div>

                    {isMobile === true && renderExtraInfo}
                </div>
            </div>

            {isMobile ? (
                <StickTopPanel>
                    <GoToShopButton
                        {...props.navigateButton}
                        brandName={props.brandName}
                        buttonStyleX={styles.stickyButton}
                    />
                </StickTopPanel>
            ) : (
                <GoToShopButton {...props.navigateButton} brandName={props.brandName} />
            )}

            {renderNewCashbackInfo}
            {modal()}
        </div>
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    buttons: {
        width: "100%",
    },
    newCashbackUser: {
        padding: { default: "30px 50px", [xsMax]: "30px 40px" },
        borderColor: colors.b_BrandMagenta,
        borderRadius: "10px",
        borderStyle: "solid",
        borderWidth: "1px",
        color: "#222",
        textAlign: "center",
    },
    newUserText: {
        display: "inline-block",
        fontSize: "1rem",
        lineHeight: 1.2,
        verticalAlign: "middle",
    },
    title: {
        fontWeight: 500,
    },
    plus: {
        margin: { default: "30px 0", [xsMax]: "20px 0" },
        alignItems: "center",
        display: "flex",
        fontSize: "2rem",
        justifyContent: "center",
        height: "15px",
    },

    cashbackIcon: {
        color: colors.b_BrandMagenta,
        flexBasis: "15%",
        height: 25,
        width: 25,
    },

    infoIcon: {
        color: colors.b_BrandMagenta,
        flexShrink: 0,
        height: 18,
        width: 18,
    },

    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },
    border25: {
        borderRadius: "1.5625rem",
    },

    stickyButton: {
        borderRadius: ComposableSpacing.s1,
    },
});
